import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Save } from 'lucide-react';
import { useAdminStore } from '@/lib/admin-store';
import AdminBilingualInput from '@/components/admin/AdminBilingualInput';
import AdminImageUpload from '@/components/admin/AdminImageUpload';
import toast from 'react-hot-toast';
import type { BilingualText } from '@/types';

const INPUT_CLS = "w-full px-4 py-3 bg-black/[0.03] dark:bg-white/[0.05] border border-black/[0.06] dark:border-white/[0.08] rounded-xl text-sm font-medium outline-none focus:border-black/20 dark:focus:border-white/20 transition-all placeholder:text-black/20 dark:placeholder:text-white/20";
const LABEL_CLS = "block text-[9px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40 mb-2";

const AdminTeamEditorPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const teamMembers = useAdminStore((s) => s.teamMembers);
  const addTeamMember = useAdminStore((s) => s.addTeamMember);
  const updateTeamMember = useAdminStore((s) => s.updateTeamMember);
  const fieldLimits = useAdminStore((s) => s.settings?.fieldLimits);

  const isEditing = id !== undefined && id !== 'new';
  const existing = isEditing ? teamMembers.find((m) => m._id === id) : undefined;

  const [name, setName] = useState<BilingualText>({ en: '', ka: '' });
  const [role, setRole] = useState<BilingualText>({ en: '', ka: '' });
  const [bio, setBio] = useState<BilingualText>({ en: '', ka: '' });
  const [photo, setPhoto] = useState('');

  useEffect(() => {
    if (existing) {
      setName(existing.name);
      setRole(existing.role);
      setBio(existing.bio);
      setPhoto(existing.photo);
    }
  }, [existing]);

  const handleSave = (): void => {
    try {
      if (!name.en.trim()) { toast.error('Name (EN) is required'); return; }
      if (!role.en.trim()) { toast.error('Role (EN) is required'); return; }
      if (isEditing && existing) {
        updateTeamMember(existing._id, { name, role, bio, photo });
        toast.success('Team member updated successfully!');
      } else {
        const maxOrder = teamMembers.reduce((max, m) => Math.max(max, m.order), 0);
        addTeamMember({ name, role, bio, photo, order: maxOrder + 1 });
        toast.success('Team member added successfully!');
      }
      navigate('/admin/team');
    } catch (err) {
      console.error('Failed to save team member:', err);
      toast.error('Failed to save team member. Please try again.');
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center gap-4">
        <Link to="/admin/team" className="w-9 h-9 rounded-xl border border-black/[0.06] dark:border-white/[0.06] flex items-center justify-center text-black/30 dark:text-white/30 hover:text-black dark:hover:text-white hover:border-black/15 dark:hover:border-white/15 transition-all">
          <ArrowLeft size={16} />
        </Link>
        <div>
          <h1 className="text-2xl font-syncopate font-bold tracking-tighter">{isEditing ? 'Edit Member' : 'Add Member'}</h1>
          <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.3em] text-black/20 dark:text-white/20">{isEditing ? existing?.name.en : 'Add a new team member'}</p>
        </div>
      </div>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }} className="bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl p-6 space-y-5">
        <AdminBilingualInput label="Name" value={name} onChange={setName} placeholder="Full name" maxLength={fieldLimits?.title} />
        <AdminBilingualInput label="Role" value={role} onChange={setRole} placeholder="e.g. Curator, Director" maxLength={fieldLimits?.shortDescription} />
        <AdminBilingualInput label="Bio" value={bio} onChange={setBio} textarea placeholder="Short biography" maxLength={fieldLimits?.description} />
        <AdminImageUpload label="Photo" value={photo} onChange={setPhoto} shape="circle" />
      </motion.div>
      <div className="flex items-center gap-3">
        <button onClick={handleSave} className="flex items-center gap-2 px-6 py-3 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.15em] rounded-xl hover:opacity-90 transition-opacity">
          <Save size={14} /> {isEditing ? 'Save Changes' : 'Add Member'}
        </button>
        <Link to="/admin/team" className="px-6 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-black/40 dark:text-white/40 hover:text-black dark:hover:text-white transition-colors">Cancel</Link>
      </div>
    </div>
  );
};

export default AdminTeamEditorPage;
